
import { defineComponent, computed, onMounted, ref, Ref } from 'vue';
import { useGettext } from '@jshmrtn/vue3-gettext';
import useSilentLogin from '@/composables/useSilentLogin';
import useNavigateTo from '@/composables/useNavigateTo';
import useHandleKey from '@/composables/useHandleKey';
import { Credentials } from '@/interfaces/credentials.interface';
import { CxServiceUnavailableError } from '@/classes/error.class';
import CxSpinner from '@/components/CxSpinner.vue';
import loginApi from '@/http/login.api';
import { ErrorType, useDisplayError } from '@/composables/useDisplayError';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'CxLogin',
  components: { CxSpinner },
  setup() {
    // Use composables and plugins
    const { isSilentLoginInProgress, isSilentRedirectInProgress, redirectToConX, silentLogin } = useSilentLogin();
    const { navigateToRecovery } = useNavigateTo();
    const { handleKeyDown } = useHandleKey();
    const language = useGettext();
    const { currentErrorType, errorText } = useDisplayError(language);
    const route = useRoute();

    // Data
    const username: Ref<string> = ref('');
    const password: Ref<string> = ref('');
    const rememberMe: Ref<boolean> = ref(true);
    const isLoginQueryInProgress: Ref<boolean> = ref(false);
    const showMyWorldLoginBtn: Ref<boolean> = ref(false);

    // Computed
    const translations = computed((): {
      welcome: string;
      username: string;
      password: string;
      remember: string;
      login: string;
      reset: string;
      myWorldLogin: string;
    } => {
      return {
        welcome: language.$gettext('Welcome to Leica ConX'),
        username: language.$gettext('Username'),
        password: language.$gettext('Password'),
        remember: language.$gettext('Remember me'),
        login: language.$gettext('Login'),
        reset: language.$gettext('Trouble logging in?'),
        myWorldLogin: language.$gettext('Login with myWorld')
      };
    });
    const canLogIn = computed((): boolean => {
      return !!username.value && !!password.value;
    });

    // Methods
    const onSilentLoginError = () => {
      currentErrorType.value = ErrorType.ServiceUnavailable;
    };
    const logIn = async (): Promise<void> => {
      try {
        isLoginQueryInProgress.value = true;
        const credentials: Credentials = {
          username: username.value,
          password: password.value,
          rememberMe: rememberMe.value
        };
        const result = await loginApi.logIn(credentials);
        if (!result) currentErrorType.value = ErrorType.InvalidCredentials;
        // on success we redirect to the ConX main page
        else redirectToConX();
      } catch (error) {
        if (error instanceof CxServiceUnavailableError) currentErrorType.value = ErrorType.ServiceUnavailable;
        else currentErrorType.value = ErrorType.UnexpectedError;
      } finally {
        isLoginQueryInProgress.value = false;
      }
    };
    const checkMyWorldSwitch = (): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tempWindow: any = window;
      const switches = tempWindow?.waffle?.SWITCHES;
      const myWorldParam = route.query.myworld;
      showMyWorldLoginBtn.value =
        (switches?.myworld_sso_login ?? false) || myWorldParam === 'true';
    };
    const navigateToMyWorld = (): void => {
      window.location.href = `/sso/authenticate/?next=${process.env.VUE_APP_FMS_UI_LOCATION}`;
    };

    // Lifecycle Hooks
    onMounted(() => {
      silentLogin(onSilentLoginError);
      checkMyWorldSwitch();
    });

    // Expose properties and methods
    return {
      // Data
      username,
      password,
      rememberMe,
      isLoginQueryInProgress,
      isSilentLoginInProgress,
      isSilentRedirectInProgress,
      currentErrorType,
      // Computed
      translations,
      canLogIn,
      errorText,
      // Methods
      logIn,
      showMyWorldLoginBtn,
      navigateToMyWorld,
      navigateToRecovery,
      handleKeyDown
    };
  }
});
